import { Component, inject, OnInit, OnDestroy, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HlmSkeleton } from '@spartan-ng/helm/skeleton';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { AppLoadingService } from '@src/app/core/services/app-loading.service';
import { SplitStorageService } from '../../../core/services/split-storage.service';
import { ThemeService } from '../../../core/services/theme.service';

@Component({
  selector: 'app-skeleton-with-progress',
  standalone: true,
  imports: [CommonModule, HlmSkeleton],
  providers: [SplitStorageService, ThemeService],
  template: `
    <div class="min-h-screen bg-background dark:bg-neutral-950 flex flex-col">
      <!-- Loading Progress Bar -->
      <div class="fixed top-0 left-0 right-0 z-50">
        <div
          class="h-1 bg-primary transition-all duration-300 ease-out"
          [style.width.%]="loadingService.loadingProgress()"
        ></div>
      </div>

      <!-- Mobile Header with Hamburger Menu -->
      <div class="lg:hidden w-full fixed top-0 left-0 right-0 z-30 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
        <div class="flex items-center justify-between px-4 py-2">
          <hlm-skeleton class="h-6 w-32 dark:bg-neutral-700" />
          <hlm-skeleton class="h-8 w-8 rounded-full dark:bg-neutral-700" />
        </div>
      </div>

      <!-- Main Container - Constrained to screen dimensions -->
      <div class="flex h-screen w-screen max-w-screen max-h-screen overflow-hidden pt-8 lg:pt-0">
        <!-- Desktop Mini Sidebar -->
        <aside class="hidden lg:flex fixed top-0 bottom-0 left-0 z-20 w-15 bg-neutral-50 dark:bg-neutral-950 flex-col items-center py-5 gap-5">
          <!-- Logo -->
          <div class="flex items-center justify-center">
            <hlm-skeleton class="h-10 w-10 rounded dark:bg-neutral-700" />
          </div>

          <!-- Spacer -->
          <div class="flex-1"></div>

          <div class="flex flex-col gap-5 items-center shrink-0">
            <!-- Chat Support -->
            <hlm-skeleton class="h-9 w-9 rounded-md dark:bg-neutral-700" />
            <!-- User Menu -->
            <hlm-skeleton class="h-9 w-9 rounded-full dark:bg-neutral-700" />
          </div>
        </aside>

        <!-- Main Content Wrapper -->
        <div class="flex flex-1 h-full max-h-full min-w-0 lg:ml-15 overflow-hidden">
          <!-- Side Navigation Menu -->
          <nav 
            class="fixed lg:relative top-0 lg:top-0 left-0 bottom-0 z-50 lg:z-0 max-h-full bg-white lg:bg-neutral-50 dark:bg-neutral-900 lg:dark:bg-neutral-950 shrink-0 overflow-y-auto"
            [style.width.px]="(sidebarSize$ | async) || 280"
          >
            <div class="p-4">
              <!-- Workspace Section -->
              <div class="mb-6">
                <hlm-skeleton class="h-12 w-full rounded-md dark:bg-neutral-700" />
                <!-- Provider Manager -->
              </div>

              <!-- Menu Section -->
              <div class="mb-6">
                <hlm-skeleton class="h-4 w-12 mb-3 dark:bg-neutral-700" />
                <!-- Menu -->
                <div class="space-y-2">
                  <div class="flex items-center space-x-3 p-2 rounded-md hover:bg-gray-50 dark:hover:bg-neutral-800">
                    <hlm-skeleton class="h-4 w-4 dark:bg-neutral-600" />
                    <hlm-skeleton class="h-4 w-20 dark:bg-neutral-600" />
                    <!-- Dashboard -->
                  </div>
                  <div class="flex items-center space-x-3 p-2 rounded-md hover:bg-gray-50 dark:hover:bg-neutral-800">
                    <hlm-skeleton class="h-4 w-4 dark:bg-neutral-600" />
                    <hlm-skeleton class="h-4 w-16 dark:bg-neutral-600" />
                    <!-- Services -->
                  </div>
                  <div class="flex items-center space-x-3 p-2 rounded-md hover:bg-gray-50 dark:hover:bg-neutral-800">
                    <hlm-skeleton class="h-4 w-4 dark:bg-neutral-600" />
                    <hlm-skeleton class="h-4 w-14 dark:bg-neutral-600" />
                    <!-- Settings -->
                  </div>
                </div>
              </div>

              <!-- Applications Section -->
              <div class="mt-8">
                <hlm-skeleton class="h-4 w-20 mb-3 dark:bg-neutral-700" />
                <!-- Applications -->
                <div class="space-y-2">
                  <div class="flex items-center justify-between p-2 rounded-md hover:bg-gray-50 dark:hover:bg-neutral-800">
                    <div class="flex items-center space-x-3">
                      <hlm-skeleton class="h-4 w-4 dark:bg-neutral-600" />
                      <hlm-skeleton class="h-4 w-12 dark:bg-neutral-600" />
                      <!-- Bookings -->
                    </div>
                  </div>
                  <div class="flex items-center space-x-3 p-2 rounded-md hover:bg-gray-50 dark:hover:bg-neutral-800">
                    <hlm-skeleton class="h-4 w-4 dark:bg-neutral-600" />
                    <hlm-skeleton class="h-4 w-16 dark:bg-neutral-600" />
                    <!-- Calendar -->
                  </div>
                </div>
              </div>
            </div>
          </nav>

          <!-- Page Content Area -->
          <main class="flex-1 min-w-0 h-full max-h-full bg-neutral-50 dark:bg-neutral-950 overflow-hidden">
            <div class="h-full max-h-full p-0 lg:p-2 overflow-hidden !pl-0">
              <div class="h-full max-h-full w-full max-w-full bg-white dark:bg-neutral-900 lg:rounded-xl lg:border border-gray-200 dark:border-neutral-800 overflow-hidden">
                <div class="h-full max-h-full w-full max-w-full overflow-y-auto overflow-x-hidden p-6">
                  <!-- Page Title -->
                  <div class="mb-6">
                    <hlm-skeleton class="h-8 w-32 mb-2 dark:bg-neutral-700" />
                    <!-- Services -->
                  </div>

                  <!-- Recently Updated Section -->
                  <div class="mb-8">
                    <hlm-skeleton class="h-5 w-36 mb-4 dark:bg-neutral-700" />
                    <!-- Recently updated -->

                    <!-- Service Cards Grid -->
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                      @for (card of [1, 2, 3, 4, 5, 6]; track card; let i = $index) {
                        <div
                          class="bg-card dark:bg-neutral-800 border border-border dark:border-neutral-700 rounded-lg overflow-hidden animate-pulse"
                          [style.animation-delay]="i * 0.1 + 's'"
                        >
                          <!-- Service Image -->
                          <hlm-skeleton class="h-32 w-full dark:bg-neutral-700" />

                          <!-- Service Info -->
                          <div class="p-3">
                            <hlm-skeleton class="h-4 w-3/4 mb-2 dark:bg-neutral-600" />
                            <!-- Service Name -->
                            <hlm-skeleton class="h-3 w-full mb-1 dark:bg-neutral-600" />
                            <!-- Category, Price info -->
                            <hlm-skeleton class="h-3 w-2/3 dark:bg-neutral-600" />
                            <!-- Additional info -->
                          </div>
                        </div>
                      }
                    </div>
                  </div>

                  <!-- All Services Section -->
                  <div>
                    <div class="flex items-center justify-between mb-4">
                      <hlm-skeleton class="h-6 w-32 dark:bg-neutral-700" />
                      <!-- All Services -->
                      <div class="flex items-center space-x-4">
                        <hlm-skeleton class="h-8 w-24 rounded-md dark:bg-neutral-700" />
                        <!-- Columns -->
                        <hlm-skeleton class="h-8 w-32 rounded-md dark:bg-neutral-700" />
                        <!-- Add Service -->
                      </div>
                    </div>

                    <!-- Search and Filters -->
                    <div class="flex items-center space-x-4 mb-4">
                      <hlm-skeleton class="h-9 w-64 rounded-md dark:bg-neutral-700" />
                      <!-- Search -->
                      <hlm-skeleton class="h-9 w-20 rounded-md dark:bg-neutral-700" />
                      <!-- Category -->
                      <hlm-skeleton class="h-9 w-16 rounded-md dark:bg-neutral-700" />
                      <!-- Status -->
                    </div>

                    <!-- Services Table -->
                    <div class="border border-border dark:border-neutral-700 rounded-lg bg-card dark:bg-neutral-800">
                      <!-- Table Header -->
                      <div class="border-b border-border dark:border-neutral-700 p-4 bg-neutral-50 dark:bg-neutral-900">
                        <div class="grid grid-cols-12 gap-4 items-center">
                          <hlm-skeleton class="h-4 w-4 dark:bg-neutral-600" />
                          <!-- Checkbox -->
                          <hlm-skeleton class="h-4 w-12 dark:bg-neutral-600" />
                          <!-- Image -->
                          <hlm-skeleton class="h-4 w-16 dark:bg-neutral-600" />
                          <!-- Name -->
                          <hlm-skeleton class="h-4 w-12 dark:bg-neutral-600" />
                          <!-- Category -->
                          <hlm-skeleton class="h-4 w-12 dark:bg-neutral-600" />
                          <!-- Price -->
                          <hlm-skeleton class="h-4 w-12 dark:bg-neutral-600" />
                          <!-- Status -->
                          <hlm-skeleton class="h-4 w-20 dark:bg-neutral-600" />
                          <!-- Organization -->
                          <hlm-skeleton class="h-4 w-16 dark:bg-neutral-600" />
                          <!-- Branch -->
                          <hlm-skeleton class="h-4 w-12 dark:bg-neutral-600" />
                          <!-- Created -->
                          <hlm-skeleton class="h-4 w-16 dark:bg-neutral-600" />
                          <!-- Updated -->
                          <hlm-skeleton class="h-4 w-8 dark:bg-neutral-600" />
                          <!-- Actions -->
                        </div>
                      </div>

                      <!-- Table Rows -->
                      <div class="divide-y divide-border dark:divide-neutral-700">
                        @for (row of [1, 2, 3, 4, 5]; track row; let i = $index) {
                          <div class="p-4 animate-pulse hover:bg-gray-50 dark:hover:bg-neutral-800" [style.animation-delay]="0.2 + i * 0.1 + 's'">
                            <div class="grid grid-cols-12 gap-4 items-center">
                              <hlm-skeleton class="h-4 w-4 dark:bg-neutral-600" />
                              <!-- Checkbox -->
                              <hlm-skeleton class="h-12 w-12 rounded dark:bg-neutral-600" />
                              <!-- Service Image -->
                              <hlm-skeleton class="h-4 w-24 dark:bg-neutral-600" />
                              <!-- Service Name -->
                              <hlm-skeleton class="h-4 w-16 dark:bg-neutral-600" />
                              <!-- Category -->
                              <hlm-skeleton class="h-4 w-8 dark:bg-neutral-600" />
                              <!-- Price -->
                              <hlm-skeleton class="h-4 w-12 dark:bg-neutral-600" />
                              <!-- Status -->
                              <hlm-skeleton class="h-4 w-16 dark:bg-neutral-600" />
                              <!-- Organization -->
                              <hlm-skeleton class="h-4 w-20 dark:bg-neutral-600" />
                              <!-- Branch -->
                              <hlm-skeleton class="h-4 w-18 dark:bg-neutral-600" />
                              <!-- Created -->
                              <hlm-skeleton class="h-4 w-32 dark:bg-neutral-600" />
                              <!-- Updated -->
                              <hlm-skeleton class="h-4 w-4 dark:bg-neutral-600" />
                              <!-- Actions -->
                            </div>
                          </div>
                        }
                      </div>

                      <!-- Table Footer -->
                      <div class="border-t border-border dark:border-neutral-700 p-4 bg-neutral-50 dark:bg-neutral-900">
                        <div class="flex items-center justify-between">
                          <hlm-skeleton class="h-4 w-32 dark:bg-neutral-600" />
                          <!-- 0 of 10 row(s) selected -->
                          <div class="flex items-center space-x-4">
                            <hlm-skeleton class="h-8 w-12 rounded-md dark:bg-neutral-600" />
                            <!-- 5 -->
                            <hlm-skeleton class="h-8 w-16 rounded-md dark:bg-neutral-600" />
                            <!-- Previous -->
                            <hlm-skeleton class="h-8 w-12 rounded-md dark:bg-neutral-600" />
                            <!-- Next -->
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      <!-- Loading Status Footer -->
      <div class="border-t border-border dark:border-neutral-800 bg-card dark:bg-neutral-900 p-3">
        <div class="flex items-center justify-between max-w-7xl mx-auto">
          <div class="flex items-center space-x-3">
            <!-- Loading Spinner -->
            <div class="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div>

            <!-- Loading Message -->
            <span class="text-sm text-muted-foreground dark:text-neutral-400">
              {{ loadingService.loadingMessage() }}
            </span>
          </div>

          <!-- Progress Percentage -->
          <div class="flex items-center space-x-2">
            <span class="text-sm font-medium text-muted-foreground dark:text-neutral-400"> {{ loadingService.loadingProgress() }}% </span>

            <!-- Progress Ring -->
            <div class="relative w-5 h-5">
              <svg class="w-5 h-5 transform -rotate-90" viewBox="0 0 20 20">
                <circle
                  cx="10"
                  cy="10"
                  r="8"
                  stroke="currentColor"
                  stroke-width="2"
                  fill="none"
                  class="text-muted-foreground/20 dark:text-neutral-600"
                />
                <circle
                  cx="10"
                  cy="10"
                  r="8"
                  stroke="currentColor"
                  stroke-width="2"
                  fill="none"
                  class="text-primary transition-all duration-300 ease-out"
                  [style.stroke-dasharray]="2 * Math.PI * 8"
                  [style.stroke-dashoffset]="2 * Math.PI * 8 * (1 - loadingService.loadingProgress() / 100)"
                  stroke-linecap="round"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        height: 100vh;
        overflow: hidden;
      }

      /* Custom pulse animation with different delays */
      .animate-pulse {
        animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
      }

      @keyframes pulse {
        0%,
        100% {
          opacity: 1;
        }
        50% {
          opacity: 0.6;
        }
      }

      /* Staggered animation delays */
      .animate-pulse:nth-child(1) {
        animation-delay: 0.1s;
      }
      .animate-pulse:nth-child(2) {
        animation-delay: 0.2s;
      }
      .animate-pulse:nth-child(3) {
        animation-delay: 0.3s;
      }
      .animate-pulse:nth-child(4) {
        animation-delay: 0.4s;
      }
      .animate-pulse:nth-child(5) {
        animation-delay: 0.5s;
      }
      .animate-pulse:nth-child(6) {
        animation-delay: 0.6s;
      }
    `,
  ],
})
export class AppSkeletonWithProgressComponent implements OnInit, OnDestroy {
  protected readonly loadingService = inject(AppLoadingService);
  protected readonly Math = Math;
  
  public sidebarSize$: Observable<number>;
  private themeSubscription!: Subscription;

  constructor(
    private splitStorageService: SplitStorageService,
    private themeService: ThemeService,
    private elementRef: ElementRef
  ) {
    this.sidebarSize$ = this.splitStorageService.config$.pipe(
      map(config => config.sidebarSize)
    );
  }

  ngOnInit(): void {
    this.themeSubscription = this.themeService.activeTheme$.subscribe(theme => {
      const hostElement = this.elementRef.nativeElement;
      if (theme === 'dark') {
        hostElement.classList.add('dark');
      } else {
        hostElement.classList.remove('dark');
      }
    });
  }

  ngOnDestroy(): void {
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
  }
} 